import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { CirclePlus } from "lucide-react";

import CenteredHeading from "../../../components/UI/headingsUI/CenteredHeading";
import GrayButton from "../../../components/UI/buttonUI/GrayButton";
import CustomInput from "../../../components/UI/PageInput/CustomInput";
import { jurisOptions, jurisArr } from "../../../utils/jurisdictionDetail";
import { getHierarchy } from "../../../functions/getHierarchy";
import DialogBox from "../../../components/UI/DialogBox";
import successIcon from "../../../assets/icons/successIcon.svg";
import PrimaryButton from "../../../components/UI/buttonUI/PrimaryButton";
import SearchDropdown from "../../../components/UI/PageInput/SearchDropdown";
import HierarchyView from "../../../components/common/HierarchyView";

const AddJurisdictionForm = ({
  hierSetup,
  sendData,
  loading,
  successResponse,
  setSuccessResponse,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm();
  const accessLevel = watch("accessLevel");
  const parentAccessId = watch("parentAccessId");

  // Modify setup data for the form fields
  const parentOptions = hierSetup[jurisArr[accessLevel - 2]] || [];
  const parentLevelName = jurisOptions[accessLevel - 2]?.name;
  const selectedLevelName = jurisOptions[accessLevel - 1]?.name;

  // Get Hierarchy View data
  const getHierData = parentOptions.find((d) => d.id === parentAccessId);
  const hierarchyList = getHierarchy(getHierData, parentLevelName);

  // API CALLING HANDLER
  const onSubmit = (data) => {
    const updatedData =
      data.accessLevel === 1
        ? (({ parentAccessId, ...rest }) => rest)(data)
        : data;
    sendData(updatedData, reset);
  };

  useEffect(() => {
    if (accessLevel) {
      setValue("parentAccessId", "");
      setValue("name", "");
    }
  }, [accessLevel]);

  return (
    <>
      <div className="form-container">
        <CenteredHeading title="Add Jurisdiction" />
        <form
          onSubmit={!loading ? (e) => handleSubmit(onSubmit)(e) : undefined}
        >
          <div className="flex flex-wrap">
            <div className="halfCol mx-auto">
              <SearchDropdown
                name="accessLevel"
                label="Select Jurisdiction *"
                keyName="name"
                id="id"
                options={jurisOptions}
                setValue={setValue}
                control={control}
                selectedVal={accessLevel}
                error={errors.accessLevel}
                showOptionId={true}
                unSort={true}
              />

              {parentOptions.length > 0 && accessLevel !== 1 && (
                <>
                  <SearchDropdown
                    name="parentAccessId"
                    label={`Select ${parentLevelName} *`}
                    keyName="name"
                    id="id"
                    options={parentOptions}
                    setValue={setValue}
                    control={control}
                    selectedVal={parentAccessId}
                    error={errors.parentAccessId}
                    showOptionId={true}
                    search={true}
                  />
                  <HierarchyView list={hierarchyList} />
                </>
              )}
              {accessLevel && (
                <CustomInput
                  type="text"
                  name="name"
                  labelText={`Add ${selectedLevelName} Name *`}
                  placeholder="Enter"
                  register={{
                    ...register("name", {
                      required: "Please Enter a name",
                    }),
                  }}
                  error={errors.name}
                />
              )}
            </div>
          </div>
          <div className="addBtn---wrap gap-2">
              <PrimaryButton
                btnText={!loading ? "Add Jurisdiction" : "Adding..."}
                loading={loading}
                Icon={CirclePlus}
                size={18}
              />
              <GrayButton
                btnText="Clear"
                width="w-40"
                onClick={() => reset()}
              />
          </div>
        </form>
        {successResponse && (
          <DialogBox
            message={` ${successResponse.name} Successfully Added!`}
            messageIcon={successIcon}
            btnText="Ok"
            closeAction={() => setSuccessResponse(null)}
          />
        )}
      </div>
    </>
  );
};

export default AddJurisdictionForm;
